import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import * as Sentry from '@sentry/angular'
import { kebabCase } from 'lodash'
import cloneDeep from 'lodash.clonedeep'
import * as moment from 'moment'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { ConvertedPhoneNumber } from 'src/app/common/components/phone-input/phone-input.component'
import { LoginType } from 'src/app/interfaces/log'
import { AppUpdateStatus, InitData, InitResponse } from 'src/app/interfaces/main'
import {
  ProfileDetailResponse,
  ProfileUpdateBody,
  User,
  UserAttachmentTypePropertyResponse,
  UserMbtiPropertyResponse,
  UserNotificationData,
  UserNotificationDataResponse,
  UserPermissionData,
  UserPrivateData,
  UserPrivateDataResponse,
  UserResponse,
  UserSurveyPropertyResponse,
} from 'src/app/interfaces/user'
import { GeneralService } from 'src/app/services/general/general.service'
import { LogService } from 'src/app/services/log/log.service'
import { environment } from 'src/environments/environment'
import { isAdsenseBot } from 'src/utils/bot'
import {
  customLocalStorage,
  getFormattedDate,
  initialUniqueNameRegex,
  notAllowedProfileNameList,
} from 'src/utils/utils'
import { v4 as uuidv4 } from 'uuid'
import { Event, UserPermissionDataUpdated } from '../event/event.service'
import { CustomHttpClient } from '../http/http.service'
import { TEST_GROUP_KEY } from '../log/experiment/experiment.service'
import { RouterService } from '../router/router.service'
import { SnackbarService } from '../snackbar/snackbar.service'
import { GenericResponse } from './../../interfaces/main'

export interface TestGroup {
  key: string
  value: string
}

const TYPES_USER_KEY = 'types_u_data'
export const uniqueNameRegex = /^(?!.*open$)[0-9a-z_-]+$/

@Injectable({
  providedIn: 'root',
})
export class UserService {
  referralUidNeeded = false
  isLoggedIn$ = new BehaviorSubject<boolean>(this.isLoggedIn)
  user$ = new BehaviorSubject<User>(this.defaultUserData())
  private _initData: InitData = this.defaultUserInitData() // permission 가지고 있는 init 데이터
  private _user: User = this.defaultUserData()
  private _userPrivateDataSubject = new Subject<Partial<UserPrivateData>>()
  private _userPrivateData: UserPrivateData | undefined
  private _userNotificationData: UserNotificationData = this.getDefaultNotificationData()
  private userSubject = new Subject<User>()
  private _testGroupSubject = new Subject<TestGroup>()
  private appOpenDate = getFormattedDate(moment().format())

  constructor(
    private http: CustomHttpClient,
    private snackbarService: SnackbarService,
    private routerService: RouterService,
    private generalService: GeneralService,
    private logService: LogService,
    private event: Event<any>
  ) {
    this.generalService.deeplink.getDeeplinkPathSubject().subscribe((path: string | undefined) => {
      this.setTestGroupFromUrl(path)
      this.logService.setUserConfig()
    })
  }

  get user(): User {
    return cloneDeep(this._user)
  }

  set user(data: User) {
    const value = { ...data }
    this._user = value
    this.user$.next(value)
  }

  get userPrivateData(): UserPrivateData | undefined {
    if (this._userPrivateData !== undefined) {
      return { ...this._userPrivateData }
    }
    return
  }
  get initData(): InitData {
    return cloneDeep(this._initData)
  }
  get userPermissionData(): UserPermissionData {
    return {
      isKakaoFriendAgreed: this._initData.isKakaoFriendAgreed,
      isContactAgreed: this._initData.isContactAgreed,
      isPushPermissionAgreed: this._initData.isPushPermissionAgreed,
      isFitReportClosedTestUser: this._initData.isFitReportClosedTestUser,
    }
  }
  set userPermissionData(data: Partial<UserPermissionData>) {
    if (Object.keys(data).length > 0) {
      for (const key in data) {
        this._initData[key] = data[key]
      }
    }
  }
  get isLoggedIn(): boolean {
    return customLocalStorage.getItem('jwt') ? true : false
  }
  get uuid(): string | undefined {
    return customLocalStorage.getItem('uuid') ?? undefined
  }
  get latestAnswerReportId(): number | undefined {
    const quizId: string | null = customLocalStorage.getItem('latest_answered_report_id')
    if (quizId) {
      return +quizId
    }
    return
  }
  set latestAnswerReportId(id: number | undefined) {
    if (id !== undefined) {
      customLocalStorage.setItem('latest_answered_report_id', `${id}`)
    }
  }

  get promotionModalActioned(): boolean {
    const result: string | null = customLocalStorage.getItem('promotion_action')
    return result !== null
  }

  set promotionModalActioned(agree: boolean) {
    customLocalStorage.setItem('promotion_action', `${agree}`)
  }

  // data migrate 완료 여부
  get dataMigrated(): string | null {
    return customLocalStorage.getItem('data_migrated')
  }

  // data: version
  set dataMigrated(data: string | null) {
    if (data !== null) {
      customLocalStorage.setItem('data_migrated', data)
    }
  }

  get userNotificationData(): UserNotificationData {
    return { ...this._userNotificationData }
  }

  set userNotificationData(data: UserNotificationData) {
    this._userNotificationData = { ...data }
  }

  getUserSubject(): Observable<User> {
    return this.userSubject.asObservable()
  }

  userDataUpdated(user: User): void {
    this.userSubject.next({ ...user })
    this.logService.setUserId(user)
  }

  getUserPrivateDataSubject(): Observable<Partial<UserPrivateData>> {
    return this._userPrivateDataSubject.asObservable()
  }

  userPrivateDataUpdated(data: Partial<UserPrivateData>) {
    if (this._userPrivateData !== undefined) {
      if (data.phoneNumber) {
        data.phoneNumber = this.removeCallingCodeOnlyKorea(data.phoneNumber)
      }
      this._userPrivateData = {
        ...this._userPrivateData,
        ...data,
      }
    }
    this._userPrivateDataSubject.next(data)
  }

  getProfile(): Observable<UserResponse> {
    return this.http.get<UserResponse>(`${environment.apiUrl}/v1/user/me`).pipe(tap((res) => this.setUser(res)))
  }

  getMyPrivateData(): Observable<UserPrivateDataResponse> {
    return this.http.get<UserPrivateDataResponse>(`${environment.apiUrl}/v1/user/me/account`).pipe(
      map((res) => {
        const { phoneNumber } = res.data
        res.data.phoneNumber = this.removeCallingCodeOnlyKorea(phoneNumber ?? '')
        this._userPrivateData = res.data
        return res
      })
    )
  }

  // 한국일 경우에만 +82(callingCode) 떼고 앞에 0 붙여주기
  removeCallingCodeOnlyKorea(phoneNumber: string): string {
    if (phoneNumber !== undefined && phoneNumber !== null && phoneNumber !== '' && phoneNumber.includes('+82')) {
      phoneNumber = phoneNumber.replace('+82', '')
      const splittedPhoneNumber = phoneNumber.split(' ')
      phoneNumber = splittedPhoneNumber[Math.max(0, splittedPhoneNumber.length - 1)]
      phoneNumber = '0' + phoneNumber.replace(/-/g, '')
    }
    return phoneNumber
  }

  getNotificationSettingData(): Observable<UserNotificationDataResponse> {
    return this.http
      .get<UserNotificationDataResponse>(`${environment.apiUrl}/v1/user/push/setting`)
      .pipe(tap((res: UserNotificationDataResponse) => (this._userNotificationData = res.data)))
  }

  /**
   * @returns return true for anonymous user
   */
  navigateToLoginIfAnonymous(type?: LoginType): boolean {
    if (!this.isLoggedIn) {
      this.routerService.navigate('login', { queryParams: { type } })
      return true
    }
    return false
  }

  /**
   * 앱 실행 시 셋팅해주는 함수
   * 강제 업데이트 여부 -> 로그인 여부에 따라 로직 처리
   * 웹일 경우는 그냥 바로 main으로
   */
  async init(): Promise<void> {
    const initData: InitResponse = await this.initializeAppData().toPromise()
    if (initData?.data !== undefined) {
      const { experiments } = initData.data
      this.logService.exp.setExpLogProperties(experiments)
    }
    this.logService.setSuperProperty('is_logged_in', this.isLoggedIn + '')
  }

  /**
   * 앱 최초 실행 시 불리는 init 함수
   * @TODO error에서 페이지 이동 처리
   */
  private initializeAppData(): Observable<InitResponse> {
    const isBot = isAdsenseBot
    return this.http
      .get<InitResponse>(`${environment.apiUrl}/v1/init`, { queryParams: isBot ? { isBot } : undefined })
      .pipe(
        tap({
          next: (res: InitResponse) => {
            this.userPermissionData = res.data
            if (isBot && res.data.jwtToken) {
              customLocalStorage.setItem('jwt', res.data.jwtToken)
            }
          },
          error: (error: HttpErrorResponse) => {
            console.error(error)
          },
        })
      )
  }

  async isPhoneNumberRegistered(): Promise<boolean> {
    if (this.userPrivateData === undefined) {
      const privateDataResponse = await this.getMyPrivateData().toPromise()
      return !!privateDataResponse.data.phoneNumber
    }
    return !!this.userPrivateData.phoneNumber
  }

  setKakaoFriendAgreed(agreed: boolean): void {
    if (this._user !== undefined) {
      this._user.isKakaoFriendAgreed = agreed
    } else {
      this.getProfile().subscribe(() => {
        if (this._user !== undefined) {
          this._user.isKakaoFriendAgreed = agreed
        }
      })
    }
  }

  updateProfile(body: Partial<ProfileUpdateBody>): Observable<UserResponse> {
    const fd = new FormData()
    // if image null, save null
    if (body.image !== undefined) {
      fd.append('image', body.image ?? '')
    }
    if (body.firstName) {
      fd.append('first_name', body.firstName)
    }
    // 성 필드는 empty string이어도 그대로 업데이트 진행. (필수값 x)
    if (body.lastName !== undefined) {
      fd.append('last_name', body.lastName)
    }
    if (body.uniqueName !== undefined) {
      fd.append('unique_name', body.uniqueName)
    }
    if (body.mbtiQuizId !== undefined) {
      fd.append('mbti_quiz_id', body.mbtiQuizId.toString())
    }
    if (body.mbtiIdealTypeQuizId !== undefined) {
      fd.append('mbti_ideal_type_quiz_id', body.mbtiIdealTypeQuizId.toString())
    }
    return this.http
      .patch<UserResponse>(`${environment.apiUrl}/v1/profile/me`, fd, { loggingText: 'update profile' })
      .pipe(tap((res) => this.setUser(res)))
  }

  /** user push 알림 옵션 업데이트 */
  updateUserNotificationSettingData(body: Partial<UserNotificationData>): Observable<void> {
    return this.http.patch(`${environment.apiUrl}/v1/user/push/setting`, body, {
      loggingText: 'update push notification settings',
    })
  }

  /**
   * user permission 업데이트
   * @TODO 웹 업데이트 필요
   */
  updateUserPermissionData(body: Partial<UserPermissionData>): Observable<void> {
    return this.http
      .patch<void>(`${environment.apiUrl}/v1/user/permission`, body, {
        loggingText: 'update user permissions(kakaoFriendAgreed, contactAgreed, ...)',
      })
      .pipe(
        tap({
          next: () => {
            this.userPermissionData = body
            this.user = {
              ...this.user,
              ...this.userPermissionData,
            }
            this.event.publish(UserPermissionDataUpdated, { ...body })
          },
          error: (error: HttpErrorResponse) => {
            console.error(error)
          },
        })
      )
  }

  /**
   * user 개인정보 업데이트
   */
  updateUserPrivateData(body: Partial<UserPrivateData>): Observable<GenericResponse> {
    if (body.languageCode) {
      body.languageCode = kebabCase(body.languageCode) as any
    }
    return this.http
      .patch<GenericResponse>(`${environment.apiUrl}/v1/user/me/account`, body, {
        loggingText: 'update user private data(email or phone)',
      })
      .pipe(
        tap(() => {
          if (body.hasOwnProperty('phoneNumber')) {
            this._user.phoneNumber = body.phoneNumber ?? undefined
          }
          this.userPrivateDataUpdated(body)
        })
      )
  }

  updatePhoneNumber(result?: ConvertedPhoneNumber): Observable<GenericResponse> | undefined {
    if (result !== undefined && result.number?.length > 0 && !result.isValid) {
      this.snackbarService.openSnackBar(SnackbarService.WRONG_INPUT_PHONE)
      return
    }

    // 빈값일 경우 전화번호 지우기
    return this.updateUserPrivateData({
      phoneNumber: result?.number ?? '',
    })
  }

  logout(): void {
    this.http.clearToken()
    this.logService.setUserId(this.user)
    this.logService.clearUserProperties()
    this.logService.setSuperProperty('is_logged_in', 'false')
    this.isLoggedIn$.next(this.isLoggedIn)
  }

  removeLatestAnsweredReport(): void {
    customLocalStorage.removeItem('latest_answered_report_id')
  }

  uniqueNameFilter(uniqueName: string): boolean {
    const regex = /^[a-z0-9._-]+$/
    if (uniqueName === '') {
      this.snackbarService.openSnackBar('프로필 이름을 입력해 주세요.')
    } else if (uniqueName.length > 12) {
      this.snackbarService.openSnackBar('최대 12자까지 입력 가능합니다.')
    } else if (uniqueName.length < 4) {
      this.snackbarService.openSnackBar('사용할 수 없는 프로필 이름입니다.')
    } else if (!regex.test(uniqueName) || initialUniqueNameRegex.test(uniqueName)) {
      this.snackbarService.openSnackBar('사용할 수 없는 프로필 이름입니다.')
    } else if (notAllowedProfileNameList.includes(uniqueName)) {
      this.snackbarService.openSnackBar('이미 사용중인 프로필 이름입니다.')
    } else {
      return true
    }
    return false
  }

  withdrawal(): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/v1/user/withdrawal`, { loggingText: 'withdrawal' }).pipe(
      tap(() => {
        this.logout()
      })
    )
  }

  // 내 프로필 대표퀴즈 목록 조회
  getProfileDetail(uniqueName: string): Observable<ProfileDetailResponse> {
    return this.http.get<ProfileDetailResponse>(`${environment.apiUrl}/v1/profile/${uniqueName}`)
  }

  getSurveyProperty(): Observable<UserSurveyPropertyResponse> {
    return this.http.get<UserSurveyPropertyResponse>(`${environment.apiUrl}/v1/communities/surveys/property/mine`).pipe(
      tap((res) => {
        this.user = { ...this.user, ...res.data }
        this.logService.setUserConfig({
          surveyFirstAnsweredAt: res.data.surveyFirstAnsweredAt
            ? getFormattedDate(res.data.surveyFirstAnsweredAt)
            : 'null',
          surveyLastAnsweredAt: res.data.surveyLastAnsweredAt
            ? getFormattedDate(res.data.surveyLastAnsweredAt)
            : 'null',
        })
      })
    )
  }

  getMbtiProperty(): Observable<UserMbtiPropertyResponse> {
    return this.http.get<UserMbtiPropertyResponse>(`${environment.apiUrl}/v2/question/report/mbti/property/mine`).pipe(
      tap((res) => {
        this.user = { ...this.user, ...res.data }
        this.logService.setUserConfig({
          mbtiFirstCompletedAt: res.data.mbtiFirstCompletedAt
            ? getFormattedDate(res.data.mbtiFirstCompletedAt)
            : 'null',
          mbtiLastCompletedAt: res.data.mbtiLastCompletedAt ? getFormattedDate(res.data.mbtiLastCompletedAt) : 'null',
          mbtiAnswerGotCount: res.data.mbtiAnswerGotCount,
        })
      })
    )
  }

  getAttachmentTypeProperty(): Observable<UserAttachmentTypePropertyResponse> {
    return this.http
      .get<UserAttachmentTypePropertyResponse>(`${environment.apiUrl}/v2/question/report/attachment-type/property/mine`)
      .pipe(
        tap((res) => {
          this.user = { ...this.user, ...res.data }
          this.logService.setUserConfig({
            userAttachmentType: res.data.attachmentType,
            attachmentFirstCompleteAt: res.data.attachmentTypeFirstCompletedAt
              ? getFormattedDate(res.data.attachmentTypeFirstCompletedAt)
              : 'null',
            attachmentLastCompleteAt: res.data.attachmentTypeLastCompletedAt
              ? getFormattedDate(res.data.attachmentTypeLastCompletedAt)
              : 'null',
          })
        })
      )
  }

  getDefaultNotificationData(): UserNotificationData {
    return {
      isPromotionAgreed: false,
      isPushNewGuestQuizOn: false,
      isPushFriendNewHostQuizOn: false,
    }
  }

  private setUser(res: UserResponse): void {
    this.user = res.data
    customLocalStorage.setItem(TYPES_USER_KEY, JSON.stringify(res.data))
    this.setUserProperties(res.data)
    Sentry.setUser({ id: String(this._user.id) })
    if (!customLocalStorage.getItem('firstName') && this._user.profile.firstName) {
      customLocalStorage.setItem('firstName', this._user.profile.firstName)
    }
    if (this._user.profile.lastName) {
      customLocalStorage.setItem('lastName', this._user.profile.lastName)
    }
    this.userDataUpdated(this._user)
    this.isLoggedIn$.next(this.isLoggedIn)
  }

  private setUserProperties(data: User): void {
    this.logService.setUserConfig({
      unique_name: data.profile.uniqueName,
      gender: data.gender ?? '',
      birth_year: data.birthYear ?? '',
      phone_number: data.phoneNumber ?? '',
      email: data.email ?? '',
      last_app_opened_at: this.appOpenDate,
      mbti_first_complete_at: data.mbtiFirstCompletedAt ? getFormattedDate(data.mbtiFirstCompletedAt) : '',
      mbti_last_complete_at: data.mbtiLastCompletedAt ? getFormattedDate(data.mbtiLastCompletedAt) : '',
      signed_up_at: data.dateJoined ? getFormattedDate(data.dateJoined) : '',
    })
  }

  private defaultUserData(): User {
    const savedUser = customLocalStorage.getItem(TYPES_USER_KEY)
    if (savedUser) {
      return JSON.parse(savedUser) as User
    }
    return {
      id: 0,
      isKakaoFriendAgreed: false,
      isContactAgreed: false,
      isPromotionAgreed: false,
      profile: {
        userId: 0,
        firstName: '',
        lastName: '',
        image: 'assets/icons/icon-default-profile.png',
        uniqueName: 'user_0',
      },
    }
  }

  private defaultUserInitData(): InitData {
    return {
      isLoggedIn: false,
      isKakaoFriendAgreed: false,
      isContactAgreed: false,
      isPromotionAgreed: false,
      isPushPermissionAgreed: false,
      updateStatus: AppUpdateStatus.LATEST,
      isFitReportClosedTestUser: false,
      experiments: [],
    }
  }

  moveToAppStore(): void {
    if (this.generalService.isAndroid) {
      this.generalService.openInappBrowser('https://play.google.com/store/apps/details?id=com.woot.types')
    } else {
      this.generalService.openInappBrowser('https://itunes.apple.com/kr/app/id1596043031')
    }
  }

  /** 이하 테스트 그룹 관련 메소드 */

  get testGroupSubject(): Observable<TestGroup> {
    return this._testGroupSubject.asObservable()
  }

  testGroup(key: string): string | undefined {
    const testGroup = customLocalStorage.getItem(key)
    return testGroup ?? undefined
  }

  /** 테스트 그룹 세팅 */
  setTestGroup(key: TEST_GROUP_KEY, value: string | undefined) {
    if (value === undefined) {
      this.removeTestGroup(key)
      return
    }
    customLocalStorage.setItem(key, value)
    this.testGroupChanged({ key, value })
  }

  /** 테스트그룹 제거 */
  removeTestGroup(key: TEST_GROUP_KEY): void {
    customLocalStorage.removeItem(key)
  }

  setTestGroupFromUrl(path: string | undefined): void {
    if (path === undefined) {
      return
    }
    const [url, queryParamString] = path.split('?')
    if (queryParamString !== undefined) {
      const queryParams: { [key: string]: string } = {}
      const splittedQueryParams = queryParamString.split('&')
      splittedQueryParams.forEach((param) => {
        const [key, value] = param.split('=')
        queryParams[key] = value
      })
      this.setTestGroupFromQueryParameter(queryParams)
    }
    if (path.includes(TEST_GROUP_KEY.REFERRAL_HOME_FEED)) {
      this.referralUidNeeded = true
    }
  }

  /** 만약 쿼리파라미터로 넘어온 테스트 그룹이 있을 경우 localStorage에 저장 */
  setTestGroupFromQueryParameter(params: { [key: string]: string }): void {
    const enumValues: string[] = Object.values(TEST_GROUP_KEY)
    Object.keys(params).forEach((key: TEST_GROUP_KEY) => {
      if (enumValues.includes(key) && params[key]) {
        this.setTestGroup(key, params[key])
      }
    })
  }

  private testGroupChanged(data: TestGroup): void {
    this._testGroupSubject.next(data)
    // testGroup을 user config에 세팅. (logging)
    this.logService.setUserConfig()
  }

  static getUUID(): string | undefined {
    return customLocalStorage.getItem('uuid') ?? undefined
  }

  // 비회원일 경우, 데이터 생성 시 uuid로 동일 인물 파악 위함.
  // 이미 생성되어 있을 경우는 제외.
  static generateUUID(): string {
    const currentId = customLocalStorage.getItem('uuid')
    if (!currentId) {
      const uuid = uuidv4()
      customLocalStorage.setItem('uuid', uuid)
      Sentry.setUser({ id: uuid })
      return uuid
    }
    Sentry.setUser({ id: currentId })
    return currentId
  }
}
